import React, { useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "./button";

// Toasts render in their own fixed region outside any dialog, so to Radix a
// tap on a toast's close or Undo is an interaction outside the dialog and
// dismisses it, taking unsaved edits with it (#134). Dialogs and drawers pass
// their outside handlers through this so the toast region doesn't count.
export function ignoreToastInteractions(handler) {
  return (event) => {
    if (event.target instanceof Element && event.target.closest("[data-toast-region]")) {
      event.preventDefault();
      return;
    }
    handler?.(event);
  };
}

export function Toast({ id, title, description, action, actions, duration = 5000, onDismiss }) {
  useEffect(() => {
    if (duration && duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(id);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [id, duration, onDismiss]);

  const actionList = actions && actions.length > 0 ? actions : action ? [action] : [];

  return (
    <div className="pointer-events-auto flex w-full max-w-sm flex-col gap-1 rounded-xl border border-border/50 bg-background/95 p-4 shadow-lg backdrop-blur-md transition-all duration-300 animate-in slide-in-from-bottom-5 fade-in-20">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          {title && <h3 className="text-sm font-semibold">{title}</h3>}
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        <button
          onClick={() => onDismiss(id)}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </div>
      {actionList.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {actionList.map((a, i) => (
            <Button
              key={i}
              size="sm"
              variant={a.variant || (i === 0 ? "default" : "secondary")}
              className="flex-1 sm:flex-initial"
              onClick={() => {
                if (a.onClick) a.onClick();
                onDismiss(id);
              }}
            >
              {a.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
